import React, { useState } from 'react';
import { LogOut, ExternalLink, User, Settings, ArrowLeft, Database, Sun, Moon, Key, FileSpreadsheet, FileDown, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight, Laptop, ShieldCheck, BookOpen, GitGraph, Building2, FileText, Presentation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useForecastContext_V2 } from '../../context/ForecastContext_V2';
import { useAuth } from '../../context/AuthContext';
import { AuthService } from '../../services/api';
import { CommercialTrashService } from '../../services/commercialTrashService';
// import { TelemetryConsoleModal } from '../common/TelemetryConsoleModal'; // SERIE 33: DESACTIVADO — hook violation. Pendiente refactor.



interface MasterTemplateProps {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    activeTab?: string;
    onBackToDashboard?: () => void;
    onExportExcel?: () => void;
    onExportPDF?: () => void;
}

export const MasterTemplate: React.FC<MasterTemplateProps> = ({
    title,
    subtitle,
    children,
    activeTab = '',
    onBackToDashboard,
    onExportExcel,
    onExportPDF
}) => {
    const navigate = useNavigate();
    const context = useForecastContext_V2();
    const { user, logout, hasPermission } = useAuth();

    // Estado para colapsar / expandir el Sidebar Izquierdo de Maestros (Permite anchar la vista de auditoría a 100%)
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
        return localStorage.getItem('petral_sidebar_collapsed') === 'true';
    });

    React.useEffect(() => {
        const handleToggleEvent = () => {
            setIsSidebarCollapsed(localStorage.getItem('petral_sidebar_collapsed') === 'true');
        };
        window.addEventListener('petral_sidebar_toggle', handleToggleEvent);
        return () => window.removeEventListener('petral_sidebar_toggle', handleToggleEvent);
    }, []);

    const toggleSidebar = () => {
        setIsSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('petral_sidebar_collapsed', String(next));
            window.dispatchEvent(new Event('petral_sidebar_toggle'));
            return next;
        });
    };

    // Mapeo inteligente de categorías para auto-expansión contextual (Estilo APEFAC)
    const categoryTabs: Record<string, string[]> = {
        herramientas: [
            'financial-matrix', 'multicotizador', 'graphic-analysis', 
            'spaghetti-map', 'voyage-liquidator', 'liquidations-graphic-analysis', 'liquidations-pdf-audit', 
            'audit-ledger', 'audit-engine', 'audit-final'
        ],
        fisicos: ['vessels', 'ports', 'routes'],
        comerciales: ['clients', 'contracts', 'quotes', 'budgets', 'financial-projections', 'commercial-trash'],
        costos: ['port-tariffs', 'port-costs', 'demurrage'],
        mercado: ['bunker', 'bunker-bootstrapping', 'sources-sinks'],
        documentacion: ['system-documentation', 'system-flowchart', 'company-organigram', 'mof-manual', 'sota-petral-one'],
        admin: ['users', 'device-vault', 'audit-ledger', 'settings']
    };

    // Estado del contador de la papelera comercial
    const [trashCount, setTrashCount] = useState<number>(0);

    React.useEffect(() => {
        const fetchTrashCount = () => {
            CommercialTrashService.getTrashCount().then(c => setTrashCount(c)).catch(() => {});
        };
        fetchTrashCount();
        window.addEventListener('commercial_trash_updated', fetchTrashCount);
        return () => window.removeEventListener('commercial_trash_updated', fetchTrashCount);
    }, []);

    const getActiveCategory = (tab: string): string => {
        for (const [cat, tabs] of Object.entries(categoryTabs)) {
            if (tabs.includes(tab)) return cat;
        }
        return 'herramientas';
    };

    const currentActiveCat = getActiveCategory(activeTab);

    // Estados de acordeón por sección: solo la sección activa inicia abierta, liberando el 75% del espacio vertical
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
        return {
            herramientas: currentActiveCat !== 'herramientas',
            fisicos: currentActiveCat !== 'fisicos',
            comerciales: currentActiveCat !== 'comerciales',
            costos: currentActiveCat !== 'costos',
            mercado: currentActiveCat !== 'mercado',
            documentacion: currentActiveCat !== 'documentacion',
            admin: currentActiveCat !== 'admin'
        };
    });

    const toggleSection = (key: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    
    // Estados para el cambio de contraseña
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [modalError, setModalError] = useState('');
    const [modalSuccess, setModalSuccess] = useState('');
    const [modalLoading, setModalLoading] = useState(false);

    const handleNewWindow = () => {
        window.open(window.location.href, '_blank');
    };


    const handleLogout = () => {
        if (confirm('¿Desea cerrar la sesión de usuario?')) {
            logout();
            navigate('/login');
        }
    };


    return (
        <div className="h-screen max-h-screen w-screen max-w-full bg-slate-50 font-sans flex flex-col antialiased overflow-hidden print:h-auto print:max-h-none print:w-full print:overflow-visible print:block print:bg-white print:p-0">
            {/* Header Superior Principal */}
            <header className="bg-white border-b border-slate-200 shrink-0 shadow-sm px-6 py-2.5 z-50 print:hidden">
                <div className="flex items-center justify-between max-w-full mx-auto w-full">
                    
                    {/* Logos y Título de App */}
                    <div className="flex items-center gap-3">
                        <img src="/Logo.Petral.png" alt="Naviera Petral" className="h-8 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
                        <div className="flex items-center border-l border-slate-200 pl-3">
                            <img src="/LOGO.PETRAL.ONE.jpg" alt="PETRAL ONE" className="object-contain cursor-pointer transition-transform hover:scale-105" style={{ height: '38px', maxHeight: '38px' }} onClick={() => navigate('/dashboard')} />
                        </div>

                        {/* Live Network Status Pill Estilo APEFAC */}
                        <div className="hidden lg:flex items-center gap-1.5 px-3 py-1 bg-emerald-50 rounded-full border border-emerald-200 shadow-2xs">
                            <span className="w-2 h-2 rounded-full bg-emerald-600 shadow-[0_0_0_2px_rgba(5,150,105,0.2)] animate-pulse"></span>
                            <span className="text-[10.5px] text-emerald-700 font-black tracking-wide uppercase">PETRAL 100% ONLINE</span>
                        </div>
                    </div>



                    {/* Título Central del Módulo */}
                    <div className="hidden md:flex flex-col items-center">
                        <div className="flex items-center gap-2">
                            <Database size={16} className="text-teal-600" />
                            <h2 className="text-base font-black text-slate-800 tracking-tight">{title}</h2>
                        </div>
                        {subtitle && <p className="text-[11px] text-slate-500 font-medium mt-0.5">{subtitle}</p>}
                    </div>

                    {/* Acciones y Panel de Usuario */}
                    <div className="flex items-center gap-4">
                        {/* Botones de Control de Ventana */}
                        <div className="flex items-center gap-1.5 border-r border-slate-200 pr-3">
                            {onExportExcel && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-50 hover:text-emerald-750 border-slate-200 text-slate-600 cursor-pointer"
                                    onClick={onExportExcel}
                                    title="Exportar datos a Excel"
                                >
                                    <FileSpreadsheet size={13} className="text-emerald-600" />
                                    <span className="hidden sm:inline">Bajar Excel</span>
                                </Button>
                            )}
                            
                            {onExportPDF && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 text-xs font-semibold flex items-center gap-1.5 hover:bg-rose-50 hover:text-rose-750 border-slate-200 text-slate-600 cursor-pointer"
                                    onClick={onExportPDF}
                                    title="Exportar datos a PDF"
                                >
                                    <FileDown size={13} className="text-rose-600" />
                                    <span className="hidden sm:inline">Bajar PDF</span>
                                </Button>
                            )}

                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 border-slate-200 text-slate-600 cursor-pointer"
                                onClick={handleNewWindow}
                                title="Abrir este módulo en una pestaña independiente"
                            >
                                <ExternalLink size={13} />
                                <span className="hidden sm:inline">Nueva Pestaña</span>
                            </Button>
                            
                            {onBackToDashboard && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 border-slate-200 text-slate-600 cursor-pointer"
                                    onClick={onBackToDashboard}
                                >
                                    <ArrowLeft size={13} />
                                    <span className="hidden sm:inline">Volver a Dashboard</span>
                                </Button>
                            )}
                        </div>

                        {/* Widget de Usuario y Logo Geeksoft */}
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-end hidden lg:flex">
                                <span className="text-xs font-bold text-slate-800 leading-tight">{user?.full_name || 'Cargando...'}</span>
                                <span className="text-[9px] font-black text-teal-600 uppercase tracking-wider">{user?.role === 'ADMIN' ? 'Administrador' : 'Usuario'}</span>
                            </div>

                            
                            {/* Avatar Dinámico con Iniciales y Degradado Premium */}
                            <div 
                                className={`h-8 w-8 rounded-full bg-gradient-to-br ${
                                    user?.role === 'ADMIN' 
                                        ? 'from-indigo-600 to-violet-500 text-white border-indigo-200' 
                                        : 'from-blue-600 to-teal-500 text-white border-blue-200'
                                } flex items-center justify-center text-xs font-black shadow-sm tracking-wider uppercase border select-none`}
                                title={`${user?.full_name} (${user?.role})`}
                            >
                                {(() => {
                                    if (!user?.full_name) return 'U';
                                    const parts = user.full_name.trim().split(/\s+/);
                                    if (parts.length >= 2) {
                                        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                                    }
                                    return parts[0][0].toUpperCase();
                                })()}
                            </div>


                            {/* Botón Cambiar Contraseña */}
                            <button 
                                onClick={() => {
                                    setModalError('');
                                    setModalSuccess('');
                                    setShowChangePasswordModal(true);
                                }}
                                className="p-1.5 rounded-full hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors border border-transparent hover:border-blue-100 cursor-pointer mr-1"
                                title="Cambiar Contraseña"
                            >
                                <Key size={16} />
                            </button>

                            {/* Botón Salir */}
                            <button 
                                onClick={handleLogout}
                                className="p-1.5 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors border border-transparent hover:border-red-100 cursor-pointer mr-1"
                                title="Cerrar Sesión"
                            >
                                <LogOut size={16} />
                            </button>

                            <span className="text-slate-200 text-lg">|</span>
                            
                            {/* Theme Toggle */}
                            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 shadow-inner ml-1">
                                <button
                                    onClick={() => context.setIsDarkMode(false)}
                                    className={`p-1.5 rounded-full transition-all ${!context.isDarkMode ? 'bg-white shadow text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                                    title="Light Mode"
                                >
                                    <Sun size={14} />
                                </button>
                                <button
                                    onClick={() => context.setIsDarkMode(true)}
                                    className={`p-1.5 rounded-full transition-all ${context.isDarkMode ? 'bg-slate-800 shadow text-indigo-400' : 'text-slate-400 hover:text-slate-600'}`}
                                    title="Dark Mode"
                                >
                                    <Moon size={14} />
                                </button>
                            </div>

                            <img src="/Logo.Geeksoft.png" alt="Geeksoft" className="h-9 object-contain pl-1" />
                        </div>
                    </div>

                </div>
            </header>

            {/* PESTAÑA / RIBBON FLOTANTE VERTICAL CUANDO EL MENÚ ESTÁ COLAPSADO */}
            {isSidebarCollapsed && (
                <button
                    onClick={toggleSidebar}
                    className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-white hover:bg-slate-100 text-slate-700 py-4 px-2 rounded-r-lg shadow-md flex flex-col items-center gap-2 text-[10px] font-extrabold uppercase tracking-wider cursor-pointer border border-l-0 border-slate-300 transition-all hover:pr-3 group select-none pointer-events-auto print:hidden"
                    title="Expandir Menú Lateral de Datos Maestros"
                >
                    <PanelLeftOpen size={16} className="text-teal-600 group-hover:scale-110 transition-transform pointer-events-none" />
                    <span className="[writing-mode:vertical-lr] rotate-180 tracking-widest text-[9px] text-slate-600 font-bold pointer-events-none">EXPANDIR MENÚ MAESTROS</span>
                </button>
            )}

            {/* Layout Principal con Sidebar Lateral de Maestros */}
            <div className="flex-1 flex max-w-full w-full mx-auto p-3 gap-3 min-h-0 overflow-hidden print:block print:p-0 print:m-0 print:h-auto print:overflow-visible print:max-w-none">
                
                {/* Sidebar Izquierdo Colapsable */}
                <aside className={`${isSidebarCollapsed ? 'w-0 opacity-0 overflow-hidden p-0 m-0 border-0 pointer-events-none' : 'w-64 opacity-100'} shrink-0 flex flex-col gap-3 hidden md:flex transition-all duration-300 h-full overflow-y-auto pr-1 print:hidden`}>

                    {/* CINTA / RIBBON SUPERIOR DEL SIDEBAR DE MAESTROS */}
                    <button
                        onClick={toggleSidebar}
                        className="w-full py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center justify-between shadow-sm cursor-pointer transition-all border border-slate-300 select-none group pointer-events-auto"
                        title="Colapsar Menú Lateral hacia la Izquierda (Anchar 100% Pantalla)"
                    >
                        <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-slate-700 pointer-events-none">
                            <PanelLeftClose size={15} className="text-slate-500 group-hover:-translate-x-0.5 transition-transform" /> COLAPSAR MAESTROS
                        </span>
                        <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-mono font-bold pointer-events-none">100%</span>
                    </button>

                    {/* SECCIÓN 1: MAESTROS FÍSICOS */}
                    {hasPermission && (hasPermission('maestro_buques', 'Visor') || hasPermission('maestro_puertos', 'Visor') || hasPermission('maestro_rutas', 'Visor')) && (
                        <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                            categoryTabs.fisicos.includes(activeTab) && !collapsedSections.fisicos 
                                ? 'border-emerald-300 ring-1 ring-emerald-400/20' 
                                : 'border-slate-200'
                        }`}>
                            <button
                                onClick={() => toggleSection('fisicos')}
                                className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 shadow-2xs"></span>
                                    <span>🏗️ M. FÍSICOS</span>
                                    {categoryTabs.fisicos.includes(activeTab) && collapsedSections.fisicos && (
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Página activa aquí"></span>
                                    )}
                                </div>
                                {!collapsedSections.fisicos ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            </button>

                            {!collapsedSections.fisicos && (
                                <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                    {hasPermission('maestro_buques', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/vessels')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'vessels' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">🚢</span> Maestro de Flota
                                        </button>
                                    )}
                                    {hasPermission('maestro_puertos', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/ports')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'ports' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">⚓</span> Maestro de Puertos
                                        </button>
                                    )}
                                    {hasPermission('maestro_rutas', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/routes')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'routes' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">📏</span> Maestro de Distancias
                                        </button>
                                    )}
                                </nav>
                            )}
                        </div>
                    )}

                    {/* SECCIÓN 2: MAESTROS COMERCIALES */}
                    {hasPermission && (hasPermission('maestro_tarifas', 'Visor') || hasPermission('maestro_contratos', 'Visor') || hasPermission('maestro_rutas', 'Visor')) && (
                        <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                            categoryTabs.comerciales.includes(activeTab) && !collapsedSections.comerciales 
                                ? 'border-indigo-300 ring-1 ring-indigo-400/20' 
                                : 'border-slate-200'
                        }`}>
                            <button
                                onClick={() => toggleSection('comerciales')}
                                className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 shadow-2xs"></span>
                                    <span>💼 M. COMERCIALES</span>
                                    {categoryTabs.comerciales.includes(activeTab) && collapsedSections.comerciales && (
                                        <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" title="Página activa aquí"></span>
                                    )}
                                </div>
                                {!collapsedSections.comerciales ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            </button>

                            {!collapsedSections.comerciales && (
                                <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                    {hasPermission('maestro_tarifas', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/clients')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'clients' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">🏢</span> Maestro de Clientes
                                        </button>
                                    )}
                                    {hasPermission('maestro_contratos', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/contracts')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'contracts' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">📜</span> Maestro de Cierres
                                        </button>
                                    )}
                                    {hasPermission('maestro_rutas', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/quotes')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'quotes' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">📑</span> Maestro de Cotizaciones
                                        </button>
                                    )}
                                    {hasPermission('maestro_contratos', 'Visor') && (
                                        <>
                                            <button 
                                                onClick={() => navigate('/budgets')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'budgets' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">📊</span> Maestro de Presupuestos
                                            </button>
                                            <button 
                                                onClick={() => navigate('/financial-projections')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'financial-projections' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">📈</span> Maestro de Matrices
                                            </button>
                                        </>
                                    )}

                                    {/* PAPELERA DE RECICLAJE COMERCIAL */}
                                    <button 
                                        onClick={() => navigate('/commercial-trash')}
                                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center justify-between transition-all ${activeTab === 'commercial-trash' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs">🗑️</span> Papelera Comercial
                                        </div>
                                        {trashCount > 0 && (
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                                                activeTab === 'commercial-trash' ? 'bg-white text-blue-700' : 'bg-rose-100 text-rose-700 border border-rose-200'
                                            }`}>
                                                {trashCount}
                                            </span>
                                        )}
                                    </button>
                                </nav>
                            )}
                        </div>
                    )}

                    {/* SECCIÓN 3: MAESTROS DE COSTOS */}
                    {hasPermission && hasPermission('maestro_costos_agencia', 'Visor') && (
                        <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                            categoryTabs.costos.includes(activeTab) && !collapsedSections.costos 
                                ? 'border-amber-300 ring-1 ring-amber-400/20' 
                                : 'border-slate-200'
                        }`}>
                            <button
                                onClick={() => toggleSection('costos')}
                                className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 shadow-2xs"></span>
                                    <span>💰 M. COSTOS</span>
                                    {categoryTabs.costos.includes(activeTab) && collapsedSections.costos && (
                                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" title="Página activa aquí"></span>
                                    )}
                                </div>
                                {!collapsedSections.costos ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            </button>

                            {!collapsedSections.costos && (
                                <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                    <button 
                                        onClick={() => navigate('/port-tariffs')}
                                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'port-tariffs' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        <span className="text-xs">🏷️</span> M. Tarifas Portuarias
                                    </button>
                                    <button 
                                        onClick={() => navigate('/port-costs')}
                                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'port-costs' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        <span className="text-xs">🧮</span> M. Gastos Portuarios
                                    </button>
                                    <button 
                                        onClick={() => navigate('/demurrage')}
                                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'demurrage' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                    >
                                        <span className="text-xs">⏳</span> M. Demoras
                                    </button>
                                </nav>
                            )}
                        </div>
                    )}

                    {/* SECCIÓN 4: MERCADO & ORIGINACIÓN */}
                    {hasPermission && (hasPermission('maestro_bunker', 'Visor') || hasPermission('maestro_rutas', 'Visor')) && (
                        <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                            categoryTabs.mercado.includes(activeTab) && !collapsedSections.mercado 
                                ? 'border-rose-300 ring-1 ring-rose-400/20' 
                                : 'border-slate-200'
                        }`}>
                            <button
                                onClick={() => toggleSection('mercado')}
                                className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0 shadow-2xs"></span>
                                    <span>⛽ MERCADO</span>
                                    {categoryTabs.mercado.includes(activeTab) && collapsedSections.mercado && (
                                        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" title="Página activa aquí"></span>
                                    )}
                                </div>
                                {!collapsedSections.mercado ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            </button>

                            {!collapsedSections.mercado && (
                                <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                    {hasPermission('maestro_bunker', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/bunker-prices')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'bunker' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">⛽</span> Maestro Búnker (Legacy)
                                        </button>
                                    )}
                                    {hasPermission('maestro_bunker', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/bunker-bootstrapping')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center justify-between gap-1 transition-all ${activeTab === 'bunker-bootstrapping' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="flex items-center gap-1.5">
                                                <span className="text-xs">⚡</span> Búnker (Bootstrapping)
                                            </span>
                                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 border border-amber-500/30">
                                                V2
                                            </span>
                                        </button>
                                    )}
                                    {hasPermission('maestro_rutas', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/sources-sinks')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'sources-sinks' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">⚙️</span> Maestro Originación
                                        </button>
                                    )}
                                </nav>
                            )}
                        </div>
                    )}

                    {/* SECCIÓN 5: HERRAMIENTAS & ANÁLISIS */}
                    {hasPermission && (hasPermission('multicotizador_spot', 'Visor') || hasPermission('matriz_financiera', 'Visor')) && (
                        <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                            categoryTabs.herramientas.includes(activeTab) && !collapsedSections.herramientas 
                                ? 'border-sky-300 ring-1 ring-sky-400/20' 
                                : 'border-slate-200'
                        }`}>
                            <button
                                onClick={() => toggleSection('herramientas')}
                                className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0 shadow-2xs"></span>
                                    <span>🛠️ HERRAMIENTAS</span>
                                    {categoryTabs.herramientas.includes(activeTab) && collapsedSections.herramientas && (
                                        <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" title="Página activa aquí"></span>
                                    )}
                                </div>
                                {!collapsedSections.herramientas ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            </button>

                            {!collapsedSections.herramientas && (
                                <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                    {hasPermission('multicotizador_spot', 'Visor') && (
                                        <button 
                                            onClick={() => navigate('/multicotizador')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'multicotizador' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <span className="text-xs">⛴️</span> Voyage Calculator
                                        </button>
                                    )}
                                    {hasPermission('matriz_financiera', 'Visor') && (
                                        <>
                                            <button 
                                                onClick={() => navigate('/dashboard')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'financial-matrix' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">📊</span> Matriz Financiera
                                            </button>
                                            <button 
                                                onClick={() => navigate('/graphic-analysis')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'graphic-analysis' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">📈</span> Análisis Gráfico
                                            </button>
                                            <button 
                                                onClick={() => navigate('/spaghetti-map')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'spaghetti-map' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">🗺️</span> Spaghetti Map
                                            </button>
                                            <button 
                                                onClick={() => navigate('/voyage-liquidator')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'voyage-liquidator' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">⚖️</span> Voyage Liquidator
                                            </button>
                                            <button 
                                                onClick={() => navigate('/liquidations-graphic-analysis')}
                                                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'liquidations-graphic-analysis' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span className="text-xs">📊</span> Análisis Liquidaciones
                                            </button>
                                        </>
                                    )}
                                </nav>
                            )}
                        </div>
                    )}

                    {/* SECCIÓN 6: DOCUMENTACIÓN */}
                    <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                        categoryTabs.documentacion.includes(activeTab) && !collapsedSections.documentacion 
                            ? 'border-teal-300 ring-1 ring-teal-400/20' 
                            : 'border-slate-200'
                    }`}>
                        <button
                            onClick={() => toggleSection('documentacion')}
                            className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                        >
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0 shadow-2xs"></span>
                                <span>📚 DOCUMENTACIÓN</span>
                                {categoryTabs.documentacion.includes(activeTab) && collapsedSections.documentacion && (
                                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" title="Página activa aquí"></span>
                                )}
                            </div>
                            {!collapsedSections.documentacion ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        </button>

                        {!collapsedSections.documentacion && (
                            <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                <button 
                                    onClick={() => navigate('/system-documentation')}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'system-documentation' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <BookOpen size={14} /> Docs as Code (Manual)
                                </button>
                                <button 
                                    onClick={() => navigate('/system-flowchart')}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'system-flowchart' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <GitGraph size={14} /> Flujograma del Sistema
                                </button>
                                <button 
                                    onClick={() => navigate('/company-organigram')}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'company-organigram' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <Building2 size={14} /> Organigrama de Empresa
                                </button>
                                <button 
                                    onClick={() => navigate('/mof-manual')}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'mof-manual' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <FileText size={14} /> Manual MOF (Puestos &amp; ERP)
                                </button>
                                <button 
                                    onClick={() => navigate('/sota-petral-one')}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'sota-petral-one' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                >
                                    <Presentation size={14} /> SOTA PETRAL ONE
                                </button>
                            </nav>
                        )}
                    </div>

                    {/* SECCIÓN 7: ADMINISTRACIÓN & SEGURIDAD */}
                    <div className={`bg-white border rounded-xl shadow-2xs p-3 flex flex-col gap-2 transition-all ${
                        categoryTabs.admin.includes(activeTab) && !collapsedSections.admin 
                            ? 'border-slate-400 ring-1 ring-slate-400/20' 
                            : 'border-slate-200'
                    }`}>
                        <button
                            onClick={() => toggleSection('admin')}
                            className="w-full flex items-center justify-between text-[10.5px] font-black text-slate-700 uppercase tracking-wider px-1 cursor-pointer hover:text-slate-900 transition-colors select-none"
                        >
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-slate-700 shrink-0 shadow-2xs"></span>
                                <span>🔒 SEGURIDAD & ADMIN</span>
                                {categoryTabs.admin.includes(activeTab) && collapsedSections.admin && (
                                    <span className="w-2 h-2 rounded-full bg-slate-700 animate-pulse" title="Página activa aquí"></span>
                                )}
                            </div>
                            {!collapsedSections.admin ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        </button>

                        {!collapsedSections.admin && (
                            <nav className="flex flex-col gap-1 transition-all pt-1 border-t border-slate-100">
                                {user?.role === 'ADMIN' && (
                                    <>
                                        <button 
                                            onClick={() => navigate('/users')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <User size={14} /> Gestión de Usuarios
                                        </button>
                                        <button 
                                            onClick={() => navigate('/device-vault')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'device-vault' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <Laptop size={14} /> Device Vault (Equipos)
                                        </button>
                                        <button 
                                            onClick={() => navigate('/audit-ledger')}
                                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'audit-ledger' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                        >
                                            <ShieldCheck size={14} /> Libro de Auditoría (Logs)
                                        </button>
                                    </>
                                )}
                                <button className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'settings' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
                                    <Settings size={14} /> Temas y Estilos
                                </button>
                            </nav>
                        )}
                    </div>
                </aside>



                {/* Contenido Principal */}
                <main className={`flex-1 flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm p-4 min-w-0 min-h-0 ${
                    ['system-documentation', 'mof-manual', 'sota-petral-one'].includes(activeTab) ? 'overflow-hidden' : 'overflow-auto'
                } master-template-content print:block print:border-none print:shadow-none print:p-0 print:m-0 print:h-auto print:overflow-visible print:w-full print:rounded-none`}>
                    {children}
                </main>

            </div>

            {/* Footer Corporativo */}
            <footer className="shrink-0 bg-white border-t border-slate-200 py-2 px-6 text-center text-[10px] text-slate-400 font-semibold tracking-wider uppercase print:hidden">
                © {new Date().getFullYear()} NAVIERA PETRAL · GEEKSOFT TECHNOLOGY PARTNER
            </footer>

            {/* Modal de Cambio de Contraseña */}
            {showChangePasswordModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white border border-slate-200 w-full max-w-md rounded-2xl shadow-xl p-6 flex flex-col gap-4 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                            <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider flex items-center gap-1.5">
                                <Key size={16} className="text-blue-600" /> Cambiar Contraseña
                            </h3>
                            <button 
                                onClick={() => setShowChangePasswordModal(false)}
                                className="text-slate-405 hover:text-slate-600 rounded-full p-1"
                            >
                                <span className="text-lg font-bold">×</span>
                            </button>
                        </div>

                        {modalError && (
                            <div className="bg-rose-50 border border-rose-200 text-rose-600 rounded-lg p-3 text-xs font-semibold leading-relaxed">
                                ⚠️ {modalError}
                            </div>
                        )}

                        {modalSuccess && (
                            <div className="bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-lg p-3 text-xs font-semibold leading-relaxed">
                                ✓ {modalSuccess}
                            </div>
                        )}

                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            setModalError('');
                            setModalSuccess('');
                            
                            if (newPassword !== confirmPassword) {
                                setModalError('Las nuevas contraseñas no coinciden.');
                                return;
                            }
                            
                            if (newPassword.length < 4) {
                                setModalError('La nueva contraseña debe tener al menos 4 caracteres.');
                                return;
                            }
                            
                            setModalLoading(true);
                            try {
                                await AuthService.changePassword({
                                    email: user?.email,
                                    current_password: currentPassword,
                                    new_password: newPassword
                                });
                                setModalSuccess('Contraseña cambiada exitosamente.');
                                setCurrentPassword('');
                                setNewPassword('');
                                setConfirmPassword('');
                                setTimeout(() => setShowChangePasswordModal(false), 1800);
                            } catch (err: any) {
                                const msg = err.response?.data?.detail || 'La contraseña actual es incorrecta o hubo un problema.';
                                setModalError(msg);
                            } finally {
                                setModalLoading(false);
                            }
                        }} className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Contraseña Actual</label>
                                <input 
                                    type="password" 
                                    required 
                                    value={currentPassword} 
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    placeholder="Ingrese su clave actual"
                                    className="border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium focus:outline-none focus:border-slate-800"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nueva Contraseña</label>
                                <input 
                                    type="password" 
                                    required 
                                    value={newPassword} 
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 4 caracteres"
                                    className="border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium focus:outline-none focus:border-slate-800"
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Confirmar Nueva Contraseña</label>
                                <input 
                                    type="password" 
                                    required 
                                    value={confirmPassword} 
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    placeholder="Repita la nueva clave"
                                    className="border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium focus:outline-none focus:border-slate-800"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                                <button 
                                    type="button"
                                    onClick={() => setShowChangePasswordModal(false)}
                                    className="text-xs bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg font-bold transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    disabled={modalLoading}
                                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold transition-colors disabled:opacity-50"
                                >
                                    {modalLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* TelemetryConsoleModal DESACTIVADO — Serie 33: violación de hooks (useEffect L15 después de return null L13) */}
            {/* <TelemetryConsoleModal /> */}

        </div>
    );
};


